"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { useAnimationContext } from "@/context/animation-context";
import { cn } from "@/lib/utils";
import Typewriter from "@/ui/atoms/typewriter";
const LandingPageTypeWriter: FC = () => {
  const $ = _c(59);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [isReplaying, setIsReplaying] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 640);
      };
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const containerRef = useRef(null);
  const {
    animationStates,
    resetTypewriterAnimation
  } = useAnimationContext();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const lineHeights = useRef(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [[1, "I'm Andrew S. Ross, a full stack developer living in the"], [2, "greater Chicagoland area. Creating outstanding experiences"], [3, "for end-users and developers alike is a major driver"], [4, "of mine. I specialize in Node.js, Typescript, React, and"], [5, "Next.js with a strong foundation in package development."], [6, "Let's build something impactful together!"]];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const linesDesktop = t3 as const satisfies readonly [number, string][];
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [[1, "I'm Andrew S. Ross, a full stack developer"], [2, "living in the greater Chicagoland area."], [3, "Creating outstanding experiences"], [4, "for end-users and developers alike"], [5, "is a major driver of mine."], [6, "I specialize in Node.js, Typescript,"], [7, "React, and Next.js with a strong"], [8, "foundation in package development."], [9, "Let's build something impactful together!"]];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const linesMobile = t4 as const satisfies readonly [number, string][];
  const lines = isMobile ? linesMobile : linesDesktop;
  const initialHeight = lines.length * 32;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsTypingComplete(true);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleTypingComplete = t5;
  let t6;
  if ($[6] !== isMobile || $[7] !== lines.length) {
    t6 = () => {
      const lineHeight = isMobile ? 20 : 40;
      const spacing = isMobile ? 8 : 4;
      return lines.length * lineHeight + (lines.length - 1) * spacing;
    };
    $[6] = isMobile;
    $[7] = lines.length;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const calculateInitialHeight = t6;
  let t7;
  if ($[9] !== calculateInitialHeight || $[10] !== isMobile || $[11] !== lines.length) {
    t7 = (height, index) => {
      lineHeights.current[index] = height;
      const totalHeight = lineHeights.current.reduce(_temp, 0);
      const spacing_0 = isMobile ? 8 : 4;
      const spacingHeight = (lines.length - 1) * spacing_0;
      const initialHeight_0 = calculateInitialHeight();
      setContainerHeight(Math.max(totalHeight + spacingHeight, initialHeight_0));
    };
    $[9] = calculateInitialHeight;
    $[10] = isMobile;
    $[11] = lines.length;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleHeightChange = t7;
  let t8;
  if ($[13] !== calculateInitialHeight || $[14] !== isMobile || $[15] !== lines.length) {
    t8 = () => {
      if (lineHeights.current.length === 0) {
        return calculateInitialHeight();
      }
      const maxLineHeight = Math.max(...lineHeights.current.filter(Boolean));
      const spacing_1 = isMobile ? 8 : 4;
      return Math.max(maxLineHeight * lines.length + (lines.length - 1) * spacing_1, calculateInitialHeight());
    };
    $[13] = calculateInitialHeight;
    $[14] = isMobile;
    $[15] = lines.length;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const estimateFinalHeight = t8;
  let t10;
  let t9;
  if ($[17] !== currentLineIndex || $[18] !== isTypingComplete || $[19] !== lines.length) {
    t9 = () => {
      if (isTypingComplete && currentLineIndex < lines.length - 1) {
        const timeout = setTimeout(() => {
          setCurrentLineIndex(_temp2);
          setIsTypingComplete(false);
        }, 500);
        return () => clearTimeout(timeout);
      }
    };
    t10 = [isTypingComplete, currentLineIndex, lines.length];
    $[17] = currentLineIndex;
    $[18] = isTypingComplete;
    $[19] = lines.length;
    $[20] = t10;
    $[21] = t9;
  } else {
    t10 = $[20];
    t9 = $[21];
  }
  useEffect(t9, t10);
  let t11;
  if ($[22] !== estimateFinalHeight || $[23] !== resetTypewriterAnimation) {
    t11 = () => {
      setIsReplaying(true);
      resetTypewriterAnimation();
      setCurrentLineIndex(0);
      setIsTypingComplete(false);
      const estimatedHeight = estimateFinalHeight();
      setContainerHeight(estimatedHeight);
      setTimeout(() => {
        setIsReplaying(false);
      }, 100);
    };
    $[22] = estimateFinalHeight;
    $[23] = resetTypewriterAnimation;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleReplay = t11;
  let t12;
  if ($[25] !== calculateInitialHeight) {
    t12 = () => {
      setContainerHeight(calculateInitialHeight());
    };
    $[25] = calculateInitialHeight;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== calculateInitialHeight || $[28] !== isMobile) {
    t13 = [calculateInitialHeight, isMobile];
    $[27] = calculateInitialHeight;
    $[28] = isMobile;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <h1 className="theme-transition mb-4 text-3xl font-bold md:mb-8">Portfolio 2025</h1>;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = lines.join("\n");
  let t16;
  if ($[31] !== t15) {
    t16 = <div className="sr-only" aria-hidden="true"><p>{t15}</p></div>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = `${containerHeight}px`;
  const t18 = `${initialHeight}px`;
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = {
      height: t17,
      transition: "height 0.5s ease-in-out",
      minHeight: t18
    };
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== currentLineIndex || $[37] !== handleHeightChange || $[38] !== isMobile || $[39] !== isReplaying || $[40] !== lines) {
    t20 = lines.map((t21, index_0) => {
      const [lineNumber, text] = t21;
      return <Typewriter key={`${lineNumber}-${isReplaying}`} lineNumber={lineNumber} text={text} isMobile={isMobile} isReplaying={isReplaying} totalLines={lines.length} isCurrentLine={lineNumber === currentLineIndex + 1} className={cn("theme-transition tracking-tight", "text-base leading-snug", "sm:text-lg sm:leading-snug", "md:text-xl md:leading-normal", "lg:text-2xl")} onTypingComplete={lineNumber === currentLineIndex + 1 ? handleTypingComplete : undefined} onHeightChange={height_0 => handleHeightChange(height_0, index_0)} isActive={lineNumber <= currentLineIndex + 1} />;
    });
    $[36] = currentLineIndex;
    $[37] = handleHeightChange;
    $[38] = isMobile;
    $[39] = isReplaying;
    $[40] = lines;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t19 || $[43] !== t20) {
    t21 = <div ref={containerRef} className="mb-4 inline-flex w-full flex-col space-y-1.5 text-center md:mb-4 md:space-y-1" style={t19}>{t20}</div>;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = animationStates.hasTypewriterPlayed && !isReplaying ? 1 : 0;
  let t23;
  if ($[45] !== t22) {
    t23 = {
      opacity: t22
    };
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      duration: 0.3
    };
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const t25 = animationStates.hasTypewriterPlayed && !isReplaying ? "visible" : "invisible";
  let t26;
  if ($[48] !== t25) {
    t26 = cn("stroke-current/50 p-2 text-current/50 transition-colors hover:stroke-current hover:text-current", t25);
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <motion.svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" role="img" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 cursor-pointer sm:size-[1.125rem]"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></motion.svg>;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== handleReplay || $[52] !== t23 || $[53] !== t26) {
    t28 = <div className="absolute bottom-2 left-1/2 -translate-x-1/2 transform sm:-bottom-2.5"><motion.button initial={false} animate={t23} transition={t24} onClick={handleReplay} className={t26} aria-label="Replay animation">{t27}</motion.button></div>;
    $[51] = handleReplay;
    $[52] = t23;
    $[53] = t26;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t16 || $[56] !== t21 || $[57] !== t28) {
    t29 = <div className="flex min-h-[3rem] flex-col px-2 sm:min-h-[4rem] sm:px-4 md:px-8"><div className="mx-auto w-full max-w-4xl text-center">{t14}<div className="relative">{t16}{t21}{t28}</div></div></div>;
    $[55] = t16;
    $[56] = t21;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  return t29;
};
export default LandingPageTypeWriter;
function _temp(sum, h) {
  return sum + h;
}
function _temp2(prev) {
  return prev + 1;
}